import { useEffect, useRef, useState } from 'react';
import { patchExtractionDraft } from '../services/api';

export interface DraftConflict {
  draft_updated_by_id: string;
  draft_updated_at: string | null;
}

/**
 * Auto-save the validation draft to the backend (PROD1.4.6 #474).
 *
 * - Débounce: 1500ms after the last change.
 * - Diff check: do NOT PATCH if `payload` is identical to the last save
 *   (avoid spamming Redis/BD when React re-renders without a real change).
 * - Holder check: do NOT PATCH when `isHolder` is false (lock conflict).
 * - Cross-user guard (#474bis): if another user holds a draft, the server
 *   returns 409 `draft_held_by_other`. The hook surfaces this via `conflict`
 *   and stops auto-saving until the caller sets `forceOverwrite=true`.
 */
export function useDebouncedDraftSave(
  documentId: string | undefined,
  payload: any,
  isHolder: boolean,
  enabled: boolean = true,
  forceOverwrite: boolean = false,
): { lastSavedAt: Date | null; conflict: DraftConflict | null } {
  const lastPayloadRef = useRef<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [conflict, setConflict] = useState<DraftConflict | null>(null);

  useEffect(() => {
    if (!documentId || !enabled || !isHolder || payload == null) return;
    if (conflict && !forceOverwrite) return;

    let serialised: string;
    try {
      serialised = JSON.stringify(payload);
    } catch {
      return;
    }
    if (serialised === lastPayloadRef.current) return;

    const timer = window.setTimeout(() => {
      lastPayloadRef.current = serialised;
      patchExtractionDraft(documentId, payload, forceOverwrite)
        .then(() => {
          setLastSavedAt(new Date());
          if (conflict) setConflict(null);
        })
        .catch((err: any) => {
          lastPayloadRef.current = null;
          const status = err?.response?.status ?? err?.status;
          const detail = err?.response?.data?.detail ?? err?.detail;
          if (status === 409 && detail?.code === 'draft_held_by_other') {
            setConflict({
              draft_updated_by_id: detail.draft_updated_by_id,
              draft_updated_at: detail.draft_updated_at ?? null,
            });
          }
        });
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [documentId, payload, isHolder, enabled, forceOverwrite, conflict]);

  return { lastSavedAt, conflict };
}
